import { memo } from "react";
import {useEffect, useState} from "react";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RestoreIcon from '@mui/icons-material/Restore';

// Reusable styles
const cardSx = { borderRadius: 3, boxShadow: 3 };
const flexItemSx = { display: "flex" };
const detailBoxSx = {
    flex: 1,
    backgroundColor: "grey.50",
    borderRadius: 2,
    p: 2,
    border: "1px solid",
    borderColor: "grey.200",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
};
const tableStyles = {
    root: { width: "100%", borderCollapse: "collapse" },
    th: {
        textAlign: "left",
        borderBottom: "1px solid #e0e0e0",
        padding: "6px",
        fontWeight: 600,
    },
    td: { padding: "6px" },
};

// Local component for an order card
export const OrderCard = memo(
    function OrderCard({
        order,
        onViewProducts,
        onViewHistory,
        viewButton = true,
        viewHistory = false,
        viewContent = true,
        defaultBorders = false,
        size = "medium",
    }) {
        const [dialogProps, setDialogProps] = useState({});

        useEffect(()=> {
            if (order && viewHistory){
                setDialogProps({
                    docId: order._id,
                    docType: "orders",
                    title: `Order #${order.id} Change History`
                })
            }
        },[order]);

        return (
            <Card sx={defaultBorders ? "" : cardSx}>
                <MDBox p={3}>
                    {/* Header */}
                    <MDBox display="flex" justifyContent="space-between" mb={size === "small" ? 1 : 3}>
                        <MDBox>
                            <MDTypography variant="h4" fontWeight="bold">
                                {`Order #${order.id}`}
                            </MDTypography>
                            <MDTypography variant="h6" color="text.secondary" fontWeight="light">
                                {order.description}
                            </MDTypography>
                        </MDBox>

                        {viewHistory && (
                            <MDBox>
                                <Tooltip title="View Change History">
                                    <IconButton
                                        onClick={() => onViewHistory(dialogProps)}
                                    >
                                        <RestoreIcon />
                                    </IconButton>
                                </Tooltip>
                            </MDBox>
                        )}

                        {viewButton && (
                            <MDBox>
                                <MDButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    onClick={() => onViewProducts?.(order)}
                                >
                                    VIEW ORDER’S PRODUCTS
                                </MDButton>
                            </MDBox>
                        )}

                    </MDBox>

                    {/* Body Grid */}
                    <Grid container spacing={size === "small" ? 1 : 2} alignItems="stretch">
                        {/* Row 1: Titles */}
                        <Grid item xs={viewContent ? 6 : 12}>
                            <MDTypography variant={size === "small" ? "h6" : "h5"} fontWeight="medium">
                                Order Details
                            </MDTypography>
                        </Grid>
                        {viewContent && (
                            <Grid item xs={6}>
                                <MDTypography variant="h5" fontWeight="medium">
                                    Order Content
                                </MDTypography>
                            </Grid>
                        )}

                        {/* Row 2: Content */}
                        <Grid item xs={viewContent ? 6 : 12} sx={flexItemSx}>
                            <MDBox sx={detailBoxSx}>
                                <MDTypography variant={size === "small" ? "body2" : "body1"} color="text.secondary">
                                    Organization: {order.organization}
                                </MDTypography>
                                <MDTypography variant={size === "small" ? "body2" : "body1"} color="text.secondary">
                                    Total Products Expected: {order.numProducts}
                                </MDTypography>
                                <MDTypography variant={size === "small" ? "body2" : "body1"} color="text.secondary">
                                    Products Assigned: {order.items.reduce((sum, item) => sum + item.qty, 0)}
                                </MDTypography>
                                <MDTypography variant={size === "small" ? "body2" : "body1"} color="text.secondary">
                                    Total Price: {order.totalPrice ? `$${order.totalPrice} CAD` : "N/A"}
                                </MDTypography>
                                <MDTypography variant={size === "small" ? "body2" : "body1"} color="text.secondary">
                                    Assigned on: {order.assignedOn}
                                </MDTypography>
                            </MDBox>
                        </Grid>

                        {viewContent && (<Grid item xs={6} sx={flexItemSx}>
                            <MDBox sx={detailBoxSx}>
                                {order.items && order.items.length > 0 ? (
                                    <table style={tableStyles.root}>
                                        <thead>
                                            <tr>
                                                <th style={tableStyles.th}>Product</th>
                                                <th style={tableStyles.th}>Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {order.items.map((row) => (
                                                <tr key={row.product}>
                                                    <td style={tableStyles.td}>{row.product}</td>
                                                    <td style={tableStyles.td}>{row.qty}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <MDTypography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ width: "100%", textAlign: "center" }}
                                    >
                                        No products assigned yet
                                    </MDTypography>
                                )}
                            </MDBox>
                        </Grid>)}
                    </Grid>
                </MDBox>
            </Card>
        );
    });